import {
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { formatCode } from "utils";
import { lang } from "../../../i18nLang";
import {
  ContractRowStore,
  MessageStore,
  ExportMeasurementStore as Store,
} from "../../../store";
import DialogModal from "../../UI/DialogModals/DialogModal";
import RadioGroupField from "../../UI/Fields/RadioGroupField";
import { LoadingSnack } from "../../UI/Messages";

const useStyle = makeStyles({
  label: {
    color: "#000000",
    marginTop: "1rem",
  },
  button: {
    textDecoration: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  icon: {
    marginTop: "0.5rem",
  },
});

const itemsPerPage = 10;
const defaultData = {
  type: "current",
  fileType: "pdf",
  filter: "false",
};

const Export = observer(
  (props: {
    /** Propriedade que indica se o modal está aberto ou fechado. */
    open: boolean;
    /** Função que será chamada ao fechar o modal */
    onClose: Function;
  }) => {
    const classes = useStyle();
    const [data, setData] = useState({ ...defaultData });
    const [processing, setProcessing] = useState(false);
    const [close, setClose] = useState(false);
    const [stages, setStages] = useState<
      { structured: string; label: string; checked: boolean }[]
    >([]);
    const [stagesCount, setStagesCount] = useState(0);

    useEffect(() => {
      setProcessing(false);
      setClose(false);
      setData({ ...defaultData });
      setStagesCount(0);

      const rows = ContractRowStore.getFirstLevelStages();
      setStages(
        rows.map((row) => ({
          structured: row.structured,
          label: `${formatCode(row.structured, "00")} ${row.description}`,
          checked: false,
        }))
      );
    }, [props.open]);

    useEffect(() => {
      async function exportRows() {
        const stagesCodes = getStagesCodes();
        const success = await Store.export(
          `${data.fileType}-${data.type}`,
          stagesCodes
        );
        // Fecha o modal em caso de sucesso
        if (success) {
          props.onClose();
        }
      }

      if (processing && ContractRowStore.allRowsLoaded && !close) {
        exportRows();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processing, ContractRowStore.allRowsLoaded, close]);

    const handleChangeInput = (ev) => {
      const { name, value } = ev.target;
      if (name && value !== undefined) {
        const newData = { ...data };
        newData[name] = value;
        if (name === "type" && value === "all") {
          newData.filter = "false";
          setStagesCount(0);
        }
        setData(newData);
      }
    };

    const handleChangeInputFilter = (ev) => {
      handleChangeInput(ev);

      const { value } = ev.target;
      const newStages = stages.map((stage) => ({ ...stage, checked: false }));

      setStages(newStages);
      setStagesCount(value === "true" ? itemsPerPage : 0);
    };

    const handleChangeStageChecked = (index: number) => {
      const newStages = [...stages];
      newStages[index].checked = !newStages[index].checked;
      setStages(newStages);
    };

    const handleClickLoad = () => setStagesCount(stagesCount + itemsPerPage);

    const handleClose = () => {
      setClose(true);
      props.onClose();
    };

    const handleConfirm = async (ev) => {
      const selectedStages = stages.filter((stage) => stage.checked);
      if (data.filter === "true" && selectedStages.length === 0) {
        MessageStore.info(lang("measurementExport.selectStageInfo"));
      } else {
        setProcessing(true);
      }
      return true; //Nao fechar a Modal
    };

    function getStagesCodes(): string[] {
      if (data.filter === "false") return [];

      return stages
        .filter((stage) => stage.checked)
        .map((stage) => stage.structured);
    }

    return (
      <DialogModal
        title={lang("measurementExport.title")}
        confirmLabel={lang("export")}
        open={props.open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        disabledConfirm={processing}
      >
        <Typography className={classes.label}>
          {lang("measurementExport.format") + ":"}
        </Typography>
        <RadioGroupField
          name="fileType"
          value={data.fileType}
          onChange={handleChangeInput}
          margin="none"
          options={[
            {
              value: "pdf",
              label: "PDF",
            },
            {
              value: "xlsx",
              label: "XLSX",
            },
          ]}
        />
        <Typography className={classes.label}>
          {lang("measurementExport.itemsToExport") + ":"}
        </Typography>
        <RadioGroupField
          name="type"
          value={data.type}
          onChange={handleChangeInput}
          margin="none"
          options={[
            {
              value: "current",
              label: lang("measurementExport.typeOptions.current"),
            },
            {
              value: "onlyMeasured",
              label: lang("measurementExport.typeOptions.onlyMeasured"),
            },
            {
              value: "all",
              label: lang("measurementExport.typeOptions.all"),
            },
          ]}
        />
        <Typography className={classes.label}>
          {lang("measurementExport.filterByStage") + ":"}
        </Typography>
        <RadioGroupField
          name="filter"
          value={data.filter}
          onChange={handleChangeInputFilter}
          margin="none"
          options={[
            {
              value: "false",
              label: lang("measurementExport.filterOptions.all"),
              disabled: data.type === "all",
            },
            {
              value: "true",
              label: lang("measurementExport.filterOptions.filter"),
              disabled: data.type === "all",
            },
          ]}
        />
        <Grid container direction="column">
          {stages.slice(0, stagesCount).map((stage, index) => (
            <FormControlLabel
              key={stage.structured}
              label={stage.label}
              control={
                <Checkbox
                  color="primary"
                  checked={stage.checked}
                  onChange={() => handleChangeStageChecked(index)}
                />
              }
            />
          ))}
        </Grid>
        {data.filter === "true" && stagesCount < stages.length && (
          <Link className={classes.button} onClick={handleClickLoad}>
            {lang("measurementExport.loadMoreStages")}
            <ArrowDropDownIcon />
          </Link>
        )}
        <LoadingSnack
          open={processing}
          message={lang("measurementExport.loadindData")}
        />
      </DialogModal>
    );
  }
);

export default Export;
